import { useState } from "react";
import { Link } from "react-router-dom";
import { Button, ModalDelete } from "expano-components";

import { Main, PageLoader, Error } from "components";
import useGet from "hooks/useGet";
import useDelete from "hooks/useDelete";

import { ReactComponent as ProductIcon } from "icons/product.svg";

import BasicData from "./components/BasicData";
import PackagesData from "./components/PackagesData";
import ChannelProducts from "./components/ChannelProducts";
import AdditionalData from "./components/AdditionalData";
import VirtualProducts from "./components/VirtualProducts";
import BundleProducts from "./components/BundleProducts";
import ProductGroup from "./components/ProductGroup";
import BrandingInfo from "./components/BrandingInfo";
import NameTranslations from "./components/NameTranslations";

const ProductEdit = ({
  match: {
    params: { id },
  },
  history,
}) => {
  const [show_delete_modal, setShowDeleteModal] = useState(false);

  const {
    is_loading,
    is_error,
    error,
    data: product,
    refetch,
  } = useGet({
    query: ["product", id],
    path: `/products/${id}`,
    context: "ui_show",
  });

  const { is_deleting, _delete } = useDelete({
    path: `/products/${id}`,
    onSuccess: () => history.push("/products"),
  });

  if (is_loading) {
    return <PageLoader />;
  }

  if (is_error) {
    return <Error message={error} />;
  }

  return (
    <Main
      page={{
        name: product?.name,
        icon: <ProductIcon />,
        breadcrumbs: [
          {
            name: "Produkty",
            path: "/products",
          },
          { name: product?.name },
        ],
        buttons: (
          <>
            <Link
              className="btn btn-sm btn-blue"
              to={`/products_manage/product_groups/${product?.product_group?.id}/statuses/products/${product.id}`}
            >
              <span>Statusy</span>
            </Link>
            <Button
              type="delete"
              text="Usuń produkt"
              disabled={is_deleting}
              onClick={() => setShowDeleteModal(true)}
            />
          </>
        ),
      }}
    >
      {show_delete_modal && (
        <ModalDelete
          handleDelete={_delete}
          handleClose={() => setShowDeleteModal(false)}
          name={`produkt ${product?.name}?`}
        />
      )}
      <div className="dashboard-wrapper-flex">
        <div className="column-half">
          <ProductGroup
            product_id={id}
            product_kind={product?.kind}
            product_group={product?.product_group}
            refetch={refetch}
          />
          <BasicData
            product_id={id}
            refetch={refetch}
            product_group={product?.product_group}
            initialValues={{
              name: product?.name,
              sku: product?.sku,
              is_bundle: product?.is_bundle,
              is_virtual: product?.is_virtual,
              has_infinite_sales_stock: product?.has_infinite_sales_stock,
              ean: product?.ean,
              producer_code: product?.producer_code,
              status: product?.status,
              suggested_price_multiplier: product?.suggested_price_multiplier,
            }}
          />
          {product?.is_virtual && (
            <VirtualProducts
              product_id={id}
              refetch={refetch}
              data={product?.product_substitutes_as_virtual}
            />
          )}
          {product?.is_bundle && (
            <BundleProducts
              product_id={id}
              refetch={refetch}
              data={product?.product_components_as_bundle}
            />
          )}
          <NameTranslations
            product_id={id}
            refetch={refetch}
            initialValues={{
              name_en: product?.name_en,
              name_de: product?.name_de,
              name_fr: product?.name_fr,
              name_es: product?.name_es,
              name_it: product?.name_it,
              name_nl: product?.name_nl,
              name_sv: product?.name_sv,
              name_bg: product?.name_bg,
              name_ro: product?.name_ro,
              name_hu: product?.name_hu,
            }}
          />
          <AdditionalData
            product_id={id}
            refetch={refetch}
            initialValues={{
              instruction_url: product?.instruction_url,
            }}
          />
          <BrandingInfo
            product_id={id}
            branding_label_url={product.branding_label_url}
            refetch={refetch}
          />
          <PackagesData
            product_id={id}
            refetch={refetch}
            initialValues={{
              weight: product?.weight,
              package_width: product?.package_width,
              package_height: product?.package_height,
              package_depth: product?.package_depth,
            }}
          />
        </div>
        <div className="column-half">
          {[
            "Allegro::Platform",
            "Amazon::Platform",
            "Ebay::Platform",
            "Wsf::Platform",
            "Erli::Platform",
            "Emag::Platform",
            "Custom::Platform",
          ].map((platform_type) => {
            const data = product?.channel_products?.filter(
              ({ channel: { platform } }) => platform?.type === platform_type,
            );

            return (
              <ChannelProducts
                key={platform_type}
                platform_type={platform_type}
                data={data}
                product_id={id}
                is_bundle={product.is_bundle}
                is_virtual={product.is_virtual}
                refetch={refetch}
              />
            );
          })}
        </div>
      </div>
    </Main>
  );
};

export default ProductEdit;
